import { useEffect } from 'react';

import { isDesktop } from '@/consts/device';
import { getAppStore, setAppStore } from '@/store';
import { ToastType } from '@/store/toast/types';
import { RightPanelTab } from '@/store/navigation/types';
import { Screen } from '@/types';
import {
  loadGenerations,
  clearGeneration,
} from '@/utils/storage/generationStorage';
import getMediaFromFlowId from '@/requests/magic-flow/getMediaFromFlowId';
import buildFlowMediaFromPollResponse from '@/requests/magic-flow/buildFlowMediaFromPollResponse';
import pollRequest, { PollStatus } from '@/requests/magic-flow/pollRequest';
import changeDriveFileName from '@/requests/cloud-storage/changeDriveFileName';
import getExtensionFromResultType from '@/utils/generation/getExtensionFromResultType';
import createContentFile from '@/utils/media/createContentFile';
import { MAGIC_FLOW_WEBHOOK_URL } from '@/consts/urls';
import { getDeeplinkSnapshot, DeeplinkKind } from '@/features/deeplinks';

import type { FlowMedia, PreviewMediaType } from '@/types';
import type { PersistedGeneration } from '@/utils/storage/generationStorage';

// Module-level flag to prevent double-execution in React Strict Mode
let hasAttemptedRecovery = false;

const recoverGeneration = async (persisted: PersistedGeneration) => {
  const { generationId, placeholderUuid, presetTitle } = persisted;

  const failAndClear = async () => {
    getAppStore().showToast('Generation failed', ToastType.Error);
    await clearGeneration(generationId);
  };

  let media: FlowMedia | null = null;
  try {
    const { response } = await pollRequest(
      `${MAGIC_FLOW_WEBHOOK_URL}/${generationId}/result`
    );
    if (response.status === PollStatus.Completed) {
      media = buildFlowMediaFromPollResponse(generationId, response);
    } else if (response.status === PollStatus.Failed) {
      await failAndClear();
      return;
    }
  } catch {
    // Status unknown — fall through and poll with a loader.
  }

  let placeholderShown = false;
  const clearPlaceholder = () => {
    if (placeholderShown) getAppStore().removeContentFiles([placeholderUuid]);
  };

  try {
    if (!media) {
      getAppStore().prependContentFile({
        uuid: placeholderUuid,
        name: 'Generating...',
        url: '',
        isLoading: true,
        generationStartTime: persisted.startedAt,
        presetType: persisted.presetType as PreviewMediaType | undefined,
      });
      placeholderShown = true;
      media = await getMediaFromFlowId(generationId);
    }

    if (!media) {
      // eslint-disable-next-line no-console
      console.log(
        '[GenerationRecovery] No result from recovered generation',
        generationId
      );
      clearPlaceholder();
      await failAndClear();
      return;
    }

    let pendingLightboxFileId: string;
    if (media.driveUid) {
      const extension = getExtensionFromResultType(media.type.toLowerCase());
      const filename = `${presetTitle || 'Generated'}.${extension}`;
      try {
        await changeDriveFileName({ id: media.driveUid, name: filename });
      } catch {
        // Non-critical
      }

      clearPlaceholder();
      getAppStore().refreshGenerations();
      pendingLightboxFileId = media.driveUid;
    } else {
      const { contentFile } = createContentFile({
        media,
        fileName: presetTitle || 'Generated',
      });
      if (placeholderShown) {
        getAppStore().updateContentFile({
          uuid: placeholderUuid,
          updates: { ...contentFile, isLoading: false },
        });
      } else {
        getAppStore().prependContentFile(contentFile);
      }
      pendingLightboxFileId = contentFile.uuid;
    }

    setAppStore({
      resultFile: media,
      generationTimestamp: Date.now(),
      pendingLightboxFileId,
      isResultMode: true,
    });

    getAppStore().setLocalNotification(media);

    await clearGeneration(generationId);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(
      '[GenerationRecovery] Failed to recover generation:',
      generationId,
      error
    );
    getAppStore().showToast('Generation failed', ToastType.Error);
    clearPlaceholder();
  }
};

const useGenerationRecovery = () => {
  useEffect(() => {
    if (hasAttemptedRecovery) return;
    hasAttemptedRecovery = true;

    // Skip if already generating (normal flow is active)
    if (getAppStore().isGenerating) return;

    // Skip when a higher-priority deeplink is active — notification handler
    // (task) or project-json autogen own that flow.
    const deeplinkKind = getDeeplinkSnapshot().kind;
    if (
      deeplinkKind === DeeplinkKind.Task ||
      deeplinkKind === DeeplinkKind.ProjectJson
    ) {
      return;
    }

    const persisted = loadGenerations();
    if (persisted.length === 0) return;

    const recoverAll = async () => {
      setAppStore({ isGenerating: true });

      // Navigate to creations
      if (isDesktop) {
        setAppStore({ rightPanelTab: RightPanelTab.Creations });
      } else {
        setAppStore({ screen: Screen.Files });
      }

      // Recover all generations in parallel
      await Promise.all(persisted.map(recoverGeneration));

      setAppStore({ isGenerating: false });
    };

    recoverAll().catch(() => {});
  }, []);
};

export default useGenerationRecovery;
